import DateDropdown from '@/components/molecules/DateDropdown';
import { URL_REGEX } from '@/constants';
import { extractDomain, formatLink } from '@/helpers/index';
import {
  UpdateCustomFieldPayload,
  useCustomField,
} from '@/hooks/useCustomField';
import { customFieldStore } from '@/state/CustomFieldStore';
import { Contact } from '@/types/contact.types';
import { Icon } from '@iconify/react';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

type EditDetailsProps = {
  data: Contact;
  handlePhoneChange: (value: any) => void;
  userData: any;
};

const EditDetails = ({
  data,
  userData,
  handlePhoneChange,
}: EditDetailsProps) => {
  const { customFields, customFieldsObject, setCustomFieldsObject } =
    customFieldStore;
  const {
    updateContactWithCustomField,
    getContactCustomFields,
    getCustomFields,
  } = useCustomField();
  const [customFieldURLError, setCustomFieldURLError] = useState(false);

  useEffect(() => {
    getCustomFields();
    getContactCustomFields(data._id as string);
  }, [data]);

  useEffect(() => {
    if (customFields?.length > 0) {
      customFields.forEach(cf => {
        const liveDataValue = data?.custom_fields?.[cf._id];
        if (customFieldsObject[cf._id] !== liveDataValue) {
          setCustomFieldsObject(cf._id, liveDataValue);
        }
      });
    }
  }, [data, customFields, setCustomFieldsObject]);

  const setFocusedInput = (inputRef: HTMLInputElement | null) => {
    userData.focusedInputRef.current = inputRef;
  };

  const onInputFocus = (inputRef: HTMLInputElement | null) => {
    setFocusedInput(inputRef);
  };

  const updateCustomField = (fieldId: string, value: string | null) => {
    const update: UpdateCustomFieldPayload = {
      contactId: data?._id as string,
      customFieldId: fieldId,
      value: value,
    };

    updateContactWithCustomField(update);
  };

  const handleKeyPress = (event: any) => {
    if (!userData?.buttonDisabled) {
      if (event.key === 'Enter') {
        userData.handleUpdateDetails();
        event.target.blur();
      }
    }
  };

  return (
    <div
      className='transition-[height] w-full mt-2'
      id='SparkHubSaveEditedDetailsForContact'
    >
      {/* Name */}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>Name</p>
        <input
          onChange={e => userData?.set_user_name(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.name ?? ''}
          type='text'
          placeholder='Add name'
          maxLength={256}
          className={`${
            data?.name === null || data?.name === '' ? '' : 'capitalize'
          } flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] border-2 border-transparent focus:border-secondary hover:border-border/30 text-black cursor-pointer`}
        />
      </div>

      {/* Email */}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>Email</p>
        <input
          onChange={e => userData?.set_email(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.email ?? ''}
          type='text'
          placeholder='Add email'
          maxLength={256}
          className={`flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
        />
      </div>

      {/* Phone */}
      <div className='flex mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500 mt-2'>
          Phone
        </p>
        <div>
          <div
            className={`w-full rounded-[4px] ${
              (!userData?.isValid && userData?.phoneFocus) ||
              !userData?.isNumAvailable
                ? 'border-red-500'
                : 'border-secondary'
            } !text-black cursor-pointer`}
          >
            <PhoneInput
              onChange={(phone: string, { country }) => {
                const dialCode = `+${country?.dialCode}`;
                let nationalNumber = phone
                  .replace(new RegExp(`^\\+?${country?.dialCode}`), '')
                  .replace(/\D/g, '');
                if (nationalNumber.startsWith('0')) {
                  nationalNumber = nationalNumber?.slice(1);
                }
                const phoneNumber = `${dialCode}${nationalNumber}`;
                handlePhoneChange(nationalNumber ? phoneNumber : '');
              }}
              onFocus={() => userData?.setPhoneFocus(true)}
              onBlur={() => userData?.setPhoneFocus(false)}
              value={userData?.phone_number ?? ''}
              placeholder='Add phone'
              inputProps={{
                name: 'phone',
                required: false,
              }}
              countrySelectorStyleProps={{
                className: '!ml-[40px] w-[40px] h-[20px]',
              }}
              inputClassName='!w-[150px] !border-none cursor-pointer'
            />
          </div>
          {!userData?.isValid && userData?.phoneFocus && (
            <p className='ml-[40px] text-[10px] text-red-500'>
              Please enter a valid phone number
            </p>
          )}
          {!userData?.isNumAvailable && (
            <p className='ml-[40px] text-[10px] text-red-500'>
              Phone number already in use
            </p>
          )}
        </div>
      </div>

      {/* Country */}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>
          Country
        </p>
        <input
          onChange={e => userData?.set_country(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.country ?? ''}
          type='text'
          placeholder='Add country'
          maxLength={256}
          className={`flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
        />
      </div>

      {/* City */}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>City</p>
        <input
          onChange={e => userData?.set_city(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.city ?? ''}
          type='text'
          placeholder='Add City'
          maxLength={256}
          className={`flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
        />
      </div>

      {/* Company Name */}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>
          Company
        </p>
        <input
          onChange={e => userData?.set_company_name(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.company_name ?? ''}
          type='text'
          placeholder='Add company name'
          maxLength={256}
          className={`${
            data?.company_name === null || data?.company_name === ''
              ? ''
              : 'capitalize'
          } flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
        />
      </div>

      {/* Company Size */}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>Size</p>
        <input
          onChange={e => userData?.set_company_size(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.company_size ?? ''}
          type='text'
          placeholder='Add company size'
          maxLength={256}
          className={`flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
        />
      </div>

      {/* Company Website*/}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>
          Website
        </p>
        <input
          onChange={e => userData?.set_company_website(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.company_website ?? ''}
          type='text'
          placeholder='Add company website'
          maxLength={256}
          className={`${
            data?.company_website === null || data?.company_website === ''
              ? ''
              : 'text-secondary'
          } flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
        />
        {data?.company_website !== null && data?.company_website !== '' && (
          <a
            href={`https://${extractDomain(data?.company_website)}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            <Icon
              icon='solar:link-bold'
              className='text-secondary text-[18px] ml-2'
            />
          </a>
        )}
      </div>

      {/* Company Industry*/}
      <div className='flex items-center mb-4'>
        <p className='mr-2 basis-24 text-md font-medium text-gray-500'>
          Industry
        </p>
        <input
          onChange={e => userData?.set_company_industry(e.target.value)}
          onKeyDown={handleKeyPress}
          value={userData?.company_industry ?? ''}
          type='text'
          placeholder='Add company industry'
          maxLength={256}
          className={`${
            data?.company_industry === null || data?.company_industry === ''
              ? ''
              : 'capitalize'
          } flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
        />
      </div>

      {customFields?.map(customField => {
        const className = clsx(
          customFieldsObject[customField._id] === null ||
            customFieldsObject[customField._id] === ''
            ? customField?.type === 'date' || customField?.type === 'number'
              ? 'w-full'
              : ''
            : 'w-full',
          customField?.type == 'checkbox'
            ? 'ml-1'
            : 'w-full px-1 hover:border-border/30 ',
          `flex-1 duration-100 ease-in-out w-full  outline-none  text-md  focus:text-[12px] rounded-[4px]  border-2 border-transparent focus:border-secondary text-black cursor-pointer`,
        );
        return (
          <div className='flex items-center mb-4' key={customField?._id}>
            <p className='mr-2 basis-24 text-md font-medium text-gray-500 whitespace-nowrap flex-none'>
              {customField?.field}
            </p>
            {customField?.type === 'select' ? (
              <select
                value={customFieldsObject[customField._id] ?? ''}
                onChange={e => {
                  const { value } = e.target;
                  customFieldStore.setCustomFieldsObject(
                    customField?._id,
                    value,
                  );
                }}
                onFocus={() => onInputFocus}
                onBlur={() =>
                  updateCustomField(
                    customField?._id,
                    customFieldsObject[customField._id],
                  )
                }
                ref={userData?.focusedSelectInputRef}
                onKeyDown={handleKeyPress}
                className={`flex-1 duration-100 ease-in-out w-full px-1 outline-none  text-md  focus:text-[12px] rounded-[4px] hover:border-border/30  border-2 border-transparent focus:border-secondary text-black cursor-pointer`}
              >
                <option value='' disabled hidden>
                  select
                </option>
                {customField?.metaData?.map((option: any) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : customField?.type === 'date' ? (
              <div
                className='flex-1'
                tabIndex={0}
                onBlur={() => {
                  const valueFromStore = customFieldsObject[customField._id];
                  if (valueFromStore !== undefined) {
                    updateCustomField(customField?._id, valueFromStore);
                  }
                }}
              >
                <DateDropdown
                  currentDate={customFieldsObject[customField._id]}
                  currentDay={parseInt(
                    customFieldsObject[customField._id]
                      ?.toString()
                      ?.split('-')[0],
                  )}
                  onSelect={date => {
                    setCustomFieldsObject(customField?._id, date);
                  }}
                />
              </div>
            ) : (
              <input
                onChange={e => {
                  const { checked, value } = e.target;
                  if (customField?.type === 'checkbox') {
                    customFieldStore.setCustomFieldsObject(
                      customField?._id,
                      checked,
                    );
                  } else if (customField?.type === 'url') {
                    setCustomFieldURLError(!URL_REGEX.test(value));
                    if (value === '') {
                      setCustomFieldURLError(false);
                    }
                    setCustomFieldsObject(customField?._id, value);
                  } else {
                    setCustomFieldsObject(customField?._id, value);
                  }
                }}
                onFocus={() => onInputFocus}
                onBlur={() => {
                  const valueFromStore = customFieldsObject[customField._id];
                  if (customField?.type === 'url') {
                    if (!customFieldURLError) {
                      const finalValue = formatLink(valueFromStore);
                      updateCustomField(customField?._id, finalValue);
                    }
                  } else if (customField?.type === 'checkbox') {
                    updateCustomField(customField?._id, String(valueFromStore));
                  } else {
                    updateCustomField(customField?._id, valueFromStore);
                  }
                }}
                ref={userData?.focusedInputRef}
                value={customFieldsObject[customField._id] ?? ''}
                checked={
                  (customFieldsObject[customField._id] ||
                    String(
                      customFieldsObject[customField._id],
                    ).toLowerCase() === 'true') &&
                  String(customFieldsObject[customField._id]).toLowerCase() !==
                    'false'
                }
                type={customField?.type}
                placeholder='Add'
                maxLength={256}
                className={className}
              />
            )}
            {customField?.type == 'url' && customFieldURLError && (
              <p className='text-[10px] text-red-500'>
                Please enter a valid url
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
export default observer(EditDetails);
